import { useCallback } from 'react';

import { gql } from '@apollo/client';
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import type { DelegatedCodingTaskPollStatus } from '@this+that/models';

const MCP_CONNECTION_FIELDS = gql`
  fragment McpConnectionFields on McpConnection {
    connectionId
    userId
    name
    serverUrl
    authType
    status
    serverInfo {
      name
      version
      protocolVersion
    }
    discoveredTools {
      name
      description
      inputSchema
      outputPropertyNames
      outputArrayPropertyNames
    }
    enabledTools
    lastSyncAt
    createdAt
    updatedAt
  }
`;

const GET_USER_MCP_CONNECTIONS = gql`
  ${MCP_CONNECTION_FIELDS}
  query getUserMcpConnections {
    getUserMcpConnections {
      ...McpConnectionFields
    }
  }
`;

const GET_MCP_CONNECTION_BY_ID = gql`
  ${MCP_CONNECTION_FIELDS}
  query getMcpConnectionById($connectionId: String!) {
    getMcpConnectionById(connectionId: $connectionId) {
      ...McpConnectionFields
    }
  }
`;

const CREATE_MCP_CONNECTION = gql`
  ${MCP_CONNECTION_FIELDS}
  mutation createMcpConnection($input: CreateMcpConnectionInput!) {
    createMcpConnection(input: $input) {
      ...McpConnectionFields
    }
  }
`;

const UPDATE_MCP_CONNECTION = gql`
  ${MCP_CONNECTION_FIELDS}
  mutation updateMcpConnection($input: UpdateMcpConnectionInput!) {
    updateMcpConnection(input: $input) {
      ...McpConnectionFields
    }
  }
`;

const DELETE_MCP_CONNECTION = gql`
  mutation deleteMcpConnection($connectionId: String!) {
    deleteMcpConnection(connectionId: $connectionId)
  }
`;

const SYNC_MCP_CONNECTION = gql`
  ${MCP_CONNECTION_FIELDS}
  mutation syncMcpConnection($connectionId: String!) {
    syncMcpConnection(connectionId: $connectionId) {
      ...McpConnectionFields
    }
  }
`;

const START_MCP_OAUTH_FLOW = gql`
  mutation startMcpOauthFlow($input: StartMcpOauthFlowInput!) {
    startMcpOauthFlow(input: $input) {
      connectionId
      authorizationUrl
    }
  }
`;

const COMPLETE_MCP_OAUTH_FLOW = gql`
  ${MCP_CONNECTION_FIELDS}
  mutation completeMcpOauthFlow($input: CompleteMcpOauthFlowInput!) {
    completeMcpOauthFlow(input: $input) {
      ...McpConnectionFields
    }
  }
`;

const CHECK_DELEGATED_CODING_TASK = gql`
  query checkDelegatedCodingTask($connectionId: String!, $jobId: String!) {
    checkDelegatedCodingTask(connectionId: $connectionId, jobId: $jobId) {
      status
      title
      prUrl
      logTail
      error
    }
  }
`;

export interface DelegatedCodingTaskStatusResult {
  status: DelegatedCodingTaskPollStatus;
  title?: string;
  prUrl?: string;
  logTail?: string;
  error?: string;
}

interface CheckDelegatedCodingTaskData {
  checkDelegatedCodingTask: DelegatedCodingTaskStatusResult;
}

interface CheckDelegatedCodingTaskVars {
  connectionId: string;
  jobId: string;
}

export interface UseCheckDelegatedCodingTaskOptions {
  pollInterval?: number;
  skip?: boolean;
}

export function useCheckDelegatedCodingTask(
  connectionId: string,
  jobId: string,
  options?: UseCheckDelegatedCodingTaskOptions,
) {
  const { data, loading, error, stopPolling } = useQuery<
    CheckDelegatedCodingTaskData,
    CheckDelegatedCodingTaskVars
  >(CHECK_DELEGATED_CODING_TASK, {
    variables: { connectionId, jobId },
    fetchPolicy: 'network-only',
    pollInterval: options?.pollInterval,
    skip: options?.skip,
  });

  return {
    result: data?.checkDelegatedCodingTask,
    loading,
    error,
    stopPolling,
  };
}

export interface McpDiscoveredTool {
  name: string;
  description?: string;
  inputSchema: Record<string, unknown>;
  outputPropertyNames: string[] | null;
  outputArrayPropertyNames: string[] | null;
}

export interface McpConnection {
  connectionId: string;
  userId: string;
  name: string;
  serverUrl: string;
  authType: string;
  status: string;
  serverInfo?: {
    name: string;
    version: string;
    protocolVersion: string;
  };
  discoveredTools: McpDiscoveredTool[];
  enabledTools: string[];
  lastSyncAt?: number;
  createdAt: number;
  updatedAt: number;
}

interface GetUserMcpConnectionsData {
  getUserMcpConnections: McpConnection[];
}

interface CreateMcpConnectionInput {
  name: string;
  serverUrl: string;
  authType: string;
  credentials?: string;
}

interface UpdateMcpConnectionInput {
  connectionId: string;
  name?: string;
  serverUrl?: string;
  authType?: string;
  credentials?: string;
  enabledTools?: string[];
}

interface StartMcpOauthFlowInput {
  serverUrl: string;
  name: string;
  connectionId?: string;
  clientId?: string;
  clientSecret?: string;
}

interface CompleteMcpOauthFlowInput {
  state: string;
  code: string;
}

export interface McpOauthFlowStart {
  connectionId: string;
  authorizationUrl: string;
}

export interface UseGetUserMcpConnectionsOptions {
  skip?: boolean;
}

export function useGetUserMcpConnections(
  options?: UseGetUserMcpConnectionsOptions,
) {
  return useQuery<GetUserMcpConnectionsData>(GET_USER_MCP_CONNECTIONS, {
    skip: options?.skip === true,
  });
}

export function useCreateMcpConnection() {
  return useMutation<unknown, { input: CreateMcpConnectionInput }>(
    CREATE_MCP_CONNECTION,
    {
      refetchQueries: [{ query: GET_USER_MCP_CONNECTIONS }],
      awaitRefetchQueries: true,
    },
  );
}

export function useUpdateMcpConnection() {
  return useMutation<unknown, { input: UpdateMcpConnectionInput }>(
    UPDATE_MCP_CONNECTION,
    {
      refetchQueries: [{ query: GET_USER_MCP_CONNECTIONS }],
      awaitRefetchQueries: true,
    },
  );
}

export function useDeleteMcpConnection() {
  return useMutation<unknown, { connectionId: string }>(DELETE_MCP_CONNECTION, {
    refetchQueries: [{ query: GET_USER_MCP_CONNECTIONS }],
    awaitRefetchQueries: true,
  });
}

export function useSyncMcpConnection() {
  const client = useApolloClient();
  const [sync, result] = useMutation<unknown, { connectionId: string }>(
    SYNC_MCP_CONNECTION,
    {
      refetchQueries: [{ query: GET_USER_MCP_CONNECTIONS }],
      awaitRefetchQueries: true,
    },
  );

  // A failed sync has already moved the connection to ERROR server-side, but Apollo only
  // runs refetchQueries when the mutation succeeds. A one-off query keeps the active
  // useQuery out of the refetch state, which the page reads as loading.
  const syncAndRefetchOnError = useCallback(
    async (options: Parameters<typeof sync>[0]) => {
      try {
        return await sync(options);
      } catch (error) {
        await client
          .query({
            query: GET_USER_MCP_CONNECTIONS,
            fetchPolicy: 'network-only',
          })
          .catch(() => undefined);
        throw error;
      }
    },
    [sync, client],
  );

  return [syncAndRefetchOnError, result] as const;
}

export function useStartMcpOauthFlow() {
  return useMutation<
    { startMcpOauthFlow: McpOauthFlowStart },
    { input: StartMcpOauthFlowInput }
  >(START_MCP_OAUTH_FLOW);
}

export function useCompleteMcpOauthFlow() {
  return useMutation<
    { completeMcpOauthFlow: McpConnection },
    { input: CompleteMcpOauthFlowInput }
  >(COMPLETE_MCP_OAUTH_FLOW, {
    refetchQueries: [{ query: GET_USER_MCP_CONNECTIONS }],
    // The callback navigates away on resolve, so the list must be fresh before it does.
    awaitRefetchQueries: true,
  });
}

export { GET_MCP_CONNECTION_BY_ID, GET_USER_MCP_CONNECTIONS };
